"use client";

import { cva, type VariantProps } from "class-variance-authority";
import { ChevronLeft, ChevronRight, Download, ExternalLink, Trash2, X, ZoomIn } from "lucide-react";
import * as React from "react";
import { cn, formatBytes } from "../lib/utils";

const galleryVariants = cva("grid gap-4", {
  variants: {
    columns: {
      2: "grid-cols-2",
      3: "grid-cols-2 sm:grid-cols-3",
      4: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-4",
      5: "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5",
      auto: "grid-cols-[repeat(auto-fill,minmax(200px,1fr))]",
    },
  },
  defaultVariants: {
    columns: 4,
  },
});

const imageVariants = cva(
  "relative rounded-lg overflow-hidden bg-muted cursor-pointer group transition-transform hover:scale-[1.02]",
  {
    variants: {
      aspect: {
        square: "aspect-square",
        video: "aspect-video",
        auto: "",
      },
    },
    defaultVariants: {
      aspect: "square",
    },
  },
);

export interface GalleryImage {
  id: string;
  src: string;
  alt?: string;
  width?: number;
  height?: number;
  size?: number;
  filename?: string;
}

export interface ImageGalleryProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect">,
    VariantProps<typeof galleryVariants>,
    VariantProps<typeof imageVariants> {
  /**
   * Array of images to display
   */
  images: GalleryImage[];
  /**
   * Whether to enable lightbox preview
   * @default true
   */
  lightbox?: boolean;
  /**
   * Whether to allow selecting images
   * @default false
   */
  selectable?: boolean;
  /**
   * Currently selected image IDs
   */
  selected?: string[];
  /**
   * Called when selection changes
   */
  onSelect?: (selected: string[]) => void;
  /**
   * Called when an image is deleted
   */
  onDelete?: (image: GalleryImage) => void;
  /**
   * Called when download is clicked
   */
  onDownload?: (image: GalleryImage) => void;
  /**
   * Whether images can be deleted
   * @default false
   */
  deletable?: boolean;
  /**
   * Whether images can be downloaded
   * @default false
   */
  downloadable?: boolean;
  /**
   * Empty state content
   */
  emptyState?: React.ReactNode;
}

export function ImageGallery({
  images,
  lightbox = true,
  selectable = false,
  selected = [],
  onSelect,
  onDelete,
  onDownload,
  deletable = false,
  downloadable = false,
  columns,
  aspect,
  emptyState,
  className,
  ...props
}: ImageGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = React.useState(false);
  const [lightboxIndex, setLightboxIndex] = React.useState(0);

  const handleImageClick = React.useCallback(
    (index: number, e: React.MouseEvent) => {
      if (selectable) {
        e.preventDefault();
        const image = images[index];
        if (!image) return;

        const isSelected = selected.includes(image.id);
        const newSelected = isSelected ? selected.filter((id) => id !== image.id) : [...selected, image.id];
        onSelect?.(newSelected);
      } else if (lightbox) {
        setLightboxIndex(index);
        setLightboxOpen(true);
      }
    },
    [images, lightbox, onSelect, selectable, selected],
  );

  const handlePrevious = React.useCallback(() => {
    setLightboxIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
  }, [images.length]);

  const handleNext = React.useCallback(() => {
    setLightboxIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
  }, [images.length]);

  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (!lightboxOpen) return;

      switch (e.key) {
        case "ArrowLeft":
          handlePrevious();
          break;
        case "ArrowRight":
          handleNext();
          break;
        case "Escape":
          setLightboxOpen(false);
          break;
      }
    },
    [lightboxOpen, handlePrevious, handleNext],
  );

  React.useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  // Prevent body scroll when lightbox is open
  React.useEffect(() => {
    if (lightboxOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [lightboxOpen]);

  if (images.length === 0 && emptyState) {
    return <>{emptyState}</>;
  }

  const currentImage = images[lightboxIndex];

  return (
    <>
      <div className={cn(galleryVariants({ columns }), className)} {...props}>
        {images.map((image, index) => {
          const isSelected = selected.includes(image.id);

          return (
            <div
              key={image.id}
              role="button"
              tabIndex={0}
              onClick={(e) => handleImageClick(index, e)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleImageClick(index, e as unknown as React.MouseEvent);
                }
              }}
              className={cn(
                imageVariants({ aspect }),
                isSelected && "ring-2 ring-primary ring-offset-2",
                "focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2",
              )}
            >
              <img
                src={image.src}
                alt={image.alt || "Gallery image"}
                className="h-full w-full object-cover"
                loading="lazy"
              />

              {/* Hover overlay */}
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                <ZoomIn className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>

              {/* Selection indicator */}
              {selectable && (
                <div
                  className={cn(
                    "absolute top-2 left-2 h-5 w-5 rounded-full border-2 transition-colors",
                    isSelected ? "bg-primary border-primary" : "bg-white/80 border-white/80",
                  )}
                >
                  {isSelected && (
                    <svg
                      className="h-full w-full text-primary-foreground"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>
              )}

              {/* Action buttons */}
              {(deletable || downloadable) && (
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {downloadable && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDownload?.(image);
                      }}
                      className="h-7 w-7 rounded bg-white/90 hover:bg-white flex items-center justify-center transition-colors"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                  )}
                  {deletable && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete?.(image);
                      }}
                      className="h-7 w-7 rounded bg-destructive/90 hover:bg-destructive text-destructive-foreground flex items-center justify-center transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Lightbox */}
      {lightbox && lightboxOpen && currentImage && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setLightboxOpen(false);
            }
          }}
          onKeyUp={(e) => {
            if (e.key === "Escape") {
              setLightboxOpen(false);
            }
          }}
        >
          {/* Close button */}
          <button
            type="button"
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
          >
            <X className="h-5 w-5 text-white" />
          </button>

          {/* Navigation */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrevious();
                }}
                className="absolute left-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleNext();
                }}
                className="absolute right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="max-w-[90vw] max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
            onKeyUp={(e) => e.stopPropagation()}
          >
            <img
              src={currentImage.src}
              alt={currentImage.alt || "Gallery image"}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>

          {/* Image info */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 text-white/80 text-sm">
            <span>
              {lightboxIndex + 1} / {images.length}
            </span>
            {currentImage.filename && <span>{currentImage.filename}</span>}
            {currentImage.size && <span>{formatBytes(currentImage.size)}</span>}
            {currentImage.width && currentImage.height && (
              <span>
                {currentImage.width} x {currentImage.height}
              </span>
            )}

            {/* External link */}
            <a
              href={currentImage.src}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      )}
    </>
  );
}

ImageGallery.displayName = "ImageGallery";
